// src/components/Hero.jsx
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import heroImage from "../../assets/raoufy.png";

const Hero = () => {
  const [showButton, setShowButton] = useState(true);
  const footerObserverRef = useRef(null);
  const projectSuspended = true;
  
  // Set up intersection observer to hide button when footer is visible
  useEffect(() => {
    // Find the footer element
    const footer = document.querySelector('footer');
    
    if (footer) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          // Hide button when footer is visible, show it otherwise
          setShowButton(!entry.isIntersecting);
        },
        { threshold: 0.1 } // Trigger when at least 10% of the footer is visible
      );
      
      observer.observe(footer);
      footerObserverRef.current = observer;
      
      // Cleanup observer when component unmounts
      return () => {
        if (footerObserverRef.current) {
          footerObserverRef.current.disconnect();
        }
      };
    }
  }, []);
  
  return (
    <>
      {/* MOBILE VERSION */}
      <section id="home" className="relative min-h-screen bg-primary overflow-hidden pt-8 md:hidden">
        {/* Subtle background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/3 left-1/4 w-72 h-72 rounded-full bg-primary-focus/30 blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full bg-primary-focus/30 blur-3xl"></div>
        </div>

        {/* Suspension Notice - MOBILE */}
        {projectSuspended && (
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 p-4 bg-error text-white rounded-lg shadow-xl text-center max-w-sm mx-auto border-2 border-red-700">
            <div className="flex items-center justify-center mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="text-xl font-bold">Projet Suspendu</h3>
            </div>
            <p className="text-sm mb-2">Ce projet est indéfiniment suspendu. Toutes les fonctionnalités sont indéfiniment désactivées.</p>
            <p className="text-xs italic">Merci de votre compréhension et de votre patience.</p>
          </div>
        )}

        {/* Sticky CTA button for mobile - always visible until footer */}
        {showButton && (
          <div className="fixed bottom-4 left-0 right-0 z-50 px-4 transition-opacity duration-300">
            <Link
              to={projectSuspended ? "#" : "/dashboard"}
              className={`button-3d text-white font-medium px-6 py-3.5 sm:px-7 sm:py-4 rounded-xl flex items-center justify-center gap-2 transition-all duration-300 w-full max-w-xs mx-auto shadow-lg text-base ${projectSuspended ? 'bg-gray-500 cursor-not-allowed opacity-70' : 'bg-warning hover:bg-warning/90'}`}
              onClick={(e) => projectSuspended && e.preventDefault()}
              aria-disabled={projectSuspended}
            >
              <span>Accéder à myPlum+</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>
        )}

        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col items-center max-w-6xl mx-auto">
            {/* Mobile layout: Text - Image - Features */}
            
            {/* 1. Greeting bubble - first on mobile, with more space from navbar */}
            <div className="greeting-bubble w-full max-w-md mx-auto mb-4 py-3 px-4 text-center mt-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-white">
                <span className="inline-block">Salut, je suis <span className="text-warning">Clico</span>! 👋</span>
              </h1>
              <p className="text-white/90 text-base sm:text-lg mt-1">Votre assistant pour naviguer la séparation</p>
            </div>
            
            {/* 2. Clico image - second on mobile, optimized for different screen sizes */}
            <div className="relative w-full mx-auto mb-5 scale-clico">
              {/* Glow effect behind character */}
              <div className="absolute inset-0 bg-gradient-radial from-warning/30 to-transparent rounded-full blur-2xl"></div>
              
              {/* Beta badge */}
              <div className="absolute -top-2 right-0 bg-warning text-white px-2 py-0.5 rounded-full text-xs font-bold shadow-lg z-20 transform rotate-12">
                BETA
              </div>
              
              {/* Robot character */}
              <img 
                src={heroImage} 
                alt="Clico - Votre assistant juridique" 
                className="relative z-10 w-full h-auto drop-shadow-2xl animate-float"
              />
            </div>
            
            {/* 3. Content section - third on mobile */}
            <div className="max-w-md w-full mb-20 sm:mb-24">
              {/* Content card - simplified for mobile */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-5 shadow-xl border border-white/20">
                <h2 className="text-lg sm:text-xl text-white font-medium mb-2 sm:mb-3">
                  Des informations juridiques pour <span className="text-warning">votre situation</span>
                </h2>
                
                <ul className="text-white/90 text-sm sm:text-base space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-warning flex-shrink-0 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Guides juridiques pour votre contexte</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-warning flex-shrink-0 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Outils d'estimation de délais et autres aides pratiques</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-warning flex-shrink-0 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Information spécifique, non personnalisée</span>
                  </li>
                </ul>
                
                {/* Developed by note */}
                <p className="text-xs text-white/60 italic mt-2">
                  *Développé avec un parajuriste en droit familial
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* DESKTOP VERSION - New layout with Clico on left */}
      <section id="home-desktop" className="hidden md:block relative min-h-screen bg-primary overflow-hidden pt-28">
        {/* Subtle background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/3 left-1/4 w-72 h-72 rounded-full bg-primary-focus/30 blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full bg-primary-focus/30 blur-3xl"></div>
        </div>

        {/* Suspension Notice - DESKTOP */}
        {projectSuspended && (
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 p-6 bg-error text-white rounded-lg shadow-xl text-center max-w-md mx-auto border-2 border-red-700">
            <div className="flex items-center justify-center mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="text-2xl font-bold">Projet Suspendu</h3>
            </div>
            <p className="mb-3">Ce projet est indéfiniment suspendu. Toutes les fonctionnalités sont indéfiniment désactivées.</p>
            <p className="text-sm italic">Merci de votre compréhension et de votre patience.</p>
          </div>
        )}

        <div className="container mx-auto px-4 relative z-10">
          {/* Top hero section with Clico on left, CTA prominent */}
          <div className="flex flex-row items-center max-w-6xl mx-auto">
            {/* Left column - Clico character */}
            <div className="w-5/12 flex justify-center">
              <div className="relative w-full max-w-md">
                {/* Glow effect behind character */}
                <div className="absolute inset-0 bg-gradient-radial from-warning/30 to-transparent rounded-full blur-2xl"></div>
                
                {/* Beta badge */}
                <div className="absolute -top-2 right-0 bg-warning text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg z-20 transform rotate-12">
                  BETA
                </div>
                
                {/* Robot character */}
                <img 
                  src={heroImage} 
                  alt="Clico - Votre assistant juridique" 
                  className="relative z-10 w-full h-auto drop-shadow-2xl animate-float"
                />
              </div>
            </div>
            
            {/* Right column - Text and CTA */}
            <div className="w-7/12 pl-10 text-left">
              {/* Greeting and introduction */}
              <div className="greeting-bubble max-w-lg mb-6 py-4 px-5">
                <h1 className="text-4xl font-bold text-white">
                  <span className="inline-block">Salut, je suis <span className="text-warning">Clico</span>! 👋</span>
                </h1>
                <p className="text-white/90 text-lg mt-2">Votre assistant pour naviguer la séparation</p>
              </div>
              
              {/* Hero description and CTA */}
              <div className="max-w-lg mb-6">
                <h2 className="text-2xl text-white font-medium mb-4">
                  Des informations juridiques pour <span className="text-warning">votre situation</span>
                </h2>
                
                <p className="text-white/90 text-lg mb-6">
                  Obtenez des informations juridiques utiles et des outils pratiques pour vous aider à naviguer votre séparation avec confiance.
                </p>
                
                {/* Desktop CTA button - prominently displayed */}
                <div>
                  <Link
                    to={projectSuspended ? "#" : "/dashboard"}
                    className={`button-3d text-white font-medium px-8 py-4 rounded-xl inline-flex items-center justify-center gap-3 transition-all duration-300 text-lg w-full max-w-sm ${projectSuspended ? 'bg-gray-500 cursor-not-allowed opacity-70' : 'bg-warning hover:bg-warning/90'}`}
                    onClick={(e) => projectSuspended && e.preventDefault()}
                    aria-disabled={projectSuspended}
                  >
                    <span>Accéder à myPlum+</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
          
          {/* Features section - below hero on desktop */}
          <div className="max-w-6xl mx-auto mt-16 mb-16">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="feature-card">
                  <div className="feature-icon mb-4">
                    <svg className="h-10 w-10 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl text-white font-medium mb-2">Guides juridiques utiles</h3>
                  <p className="text-white/80">Des informations précises et adaptées à votre contexte personnel et à vos besoins spécifiques.</p>
                </div>
                
                <div className="feature-card">
                  <div className="feature-icon mb-4">
                    <svg className="h-10 w-10 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl text-white font-medium mb-2">Outils d'estimation de délais</h3>
                  <p className="text-white/80">Des outils pratiques pour connaître les délais légaux et les étapes importantes de votre procédure.</p>
                </div>
                
                <div className="feature-card">
                  <div className="feature-icon mb-4">
                    <svg className="h-10 w-10 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl text-white font-medium mb-2">Information spécifique</h3>
                  <p className="text-white/80">Des informations non personnalisées mais pertinentes pour vous aider à prendre les bonnes décisions.</p>
                </div>
              </div>
              
              {/* Developed by note */}
              <p className="text-sm text-white/60 italic mt-6 text-center">
                *Développé avec un parajuriste en droit familial
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Animations and styles */}
      <style jsx="true">{`
        @keyframes float {
          0% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0); }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .greeting-bubble {
          position: relative;
          background: rgba(30, 58, 138, 0.5);
          border-radius: 16px;
          backdrop-filter: blur(5px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
        
        .button-3d {
          box-shadow: 
            0 4px 6px rgba(0, 0, 0, 0.2),
            inset 0 2px 2px rgba(255, 255, 255, 0.2);
          transition: all 0.2s ease;
        }
        
        .button-3d:hover {
          transform: translateY(-2px);
          box-shadow: 
            0 6px 10px rgba(0, 0, 0, 0.25),
            inset 0 2px 2px rgba(255, 255, 255, 0.2);
        }
        
        .button-3d:active {
          transform: translateY(1px);
          box-shadow: 
            0 2px 4px rgba(0, 0, 0, 0.2),
            inset 0 2px 2px rgba(255, 255, 255, 0.2);
        }
        
        /* Responsive Clico sizing */
        .scale-clico {
          max-width: 180px; /* Default for small screens like iPhone SE */
        }
        
        @media (min-width: 390px) {
          .scale-clico {
            max-width: 220px; /* Medium screens like iPhone 12/13 */
          }
        }
        
        @media (min-width: 414px) {
          .scale-clico {
            max-width: 260px; /* Larger screens like iPhone XR/11 */
          }
        }
        
        .feature-card {
          transition: transform 0.3s ease;
        }
        
        .feature-card:hover {
          transform: translateY(-5px);
        }
        
        .feature-icon {
          transition: transform 0.3s ease;
        }
        
        .feature-card:hover .feature-icon {
          transform: scale(1.1);
        }
      `}</style>
    </>
  );
};

export default Hero;
